import Footer from '@/components/Footer';
import { ChangeBookingFooterContent } from '@/components/manage/change-trips/ChangeBookingFooterContent';
import { MapModal } from '@/components/map-component/MapModal';
import { JourneysNotFound } from '@/components/purchase/journey/JourneysNotFound';
import { NonTripOffersNotFound } from '@/components/purchase/journey/NonTripOffersNotFound';
import { SearchWidgetWrapper } from '@/components/search-widget';
import { enableMapComponentSelector } from '@/features/map-component/mapComponentSelectors';
import { getPurchaseFlowBookingById } from '@/features/purchase/purchaseActions';
import {
  inboundDataSelector,
  nonTripOffersSelector,
  outboundDataSelector,
  purchaseFlowBookingSelector,
} from '@/features/purchase/purchaseSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { usePageTitle } from '@/hooks/usePageTitle';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { purchaseFlowJourneyOrOfferPaths } from '@/utils/purchase';
import { Button, Icons } from '@fleet/webshop-ui';
import { type FC, useCallback, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

interface PurchaseProps {}

const Purchase: FC<PurchaseProps> = () => {
  const dispatch = useDispatch();
  const purchaseFlowBooking = useSelector(purchaseFlowBookingSelector);
  const outboundData = useSelector(outboundDataSelector);
  const inboundData = useSelector(inboundDataSelector);
  const nonTripOffersData = useSelector(nonTripOffersSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasSelection } = useChangeBookingSelection();
  const enableMapComponent = useSelector(enableMapComponentSelector);
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  // /purchase is the only step in the change-booking flow that doesn't
  // own its own Footer (the per-step JourneyList / JourneyOfferList
  // routes render their own). Mount one here so the user can always
  // reach /manage-booking from the initial search page.
  const showChangeBookingFooter =
    hasSelection && location.pathname === '/purchase';

  usePageTitle('purchase');

  const handlePurchaseBookingFetching = useCallback(async () => {
    try {
      await dispatch(
        getPurchaseFlowBookingById(purchaseFlowBooking?.id)
      ).unwrap();
    } catch {
      await persistor.purge();
    }
  }, [dispatch, purchaseFlowBooking?.id]);

  useEffect(() => {
    const purchaseFlowBookingKeys = Object.keys(purchaseFlowBooking ?? {});

    if (purchaseFlowBooking?.id && purchaseFlowBookingKeys.length === 1) {
      handlePurchaseBookingFetching();
    } else if (purchaseFlowBookingKeys.length > 0) {
      navigate('/purchase/checkout');
    }
  }, [handlePurchaseBookingFetching, navigate, purchaseFlowBooking]);

  return (
    <PersistGate loading={null} persistor={persistor}>
      <>
        {['/purchase', ...purchaseFlowJourneyOrOfferPaths].includes(
          location.pathname
        ) && <SearchWidgetWrapper />}
        <Outlet />
        <JourneysNotFound
          outboundData={outboundData}
          inboundData={inboundData}
        />
        <NonTripOffersNotFound {...nonTripOffersData} />
        {showChangeBookingFooter && (
          <Footer
            content={<ChangeBookingFooterContent />}
            actionButtons={
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="footer-back-button"
                className="rounded-lg laptop:w-auto"
                variant="tertiary"
                onClick={() => navigate('/manage-booking')}
              >
                <Icons.arrowLeft />
                <TransText i18nKey="back" />
              </Button>
            }
          />
        )}
        {enableMapComponent && <MapModal />}
      </>
    </PersistGate>
  );
};

export default Purchase;
